/**
 * 家长点评框（见方案 3.4.4）。
 *
 * 设计要点（都来自方案里的判断）：
 *  - 点评挂在**具体对象**上，不是泛泛的留言板；
 *  - 必须提供**快捷短语** —— "家长懒得写，这个功能就会死掉"；
 *  - 低龄模式用图形化情绪标记（😄 / 🙂 / 😐）替代长文本；
 *  - 接口未就绪时显示禁用态与说明，不假装成功。
 */
import { useState } from 'react';
import { Card, CardHeader, Skeleton } from './ui/Card';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { NotReadyState } from './ui/Bits';
import {
  MOOD_LABEL,
  QUICK_PHRASES,
  createComment,
  listComments,
  type CommentMood,
} from '../lib/api/comments';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toErrorMessage } from '../lib/api/error';
import { cn, formatDateTimeFull } from '../lib/utils';
import type { Comment } from '../lib/api/types';

export interface CommentBoxProps {
  targetType: Comment['targetType'];
  targetId: string | null;
  title?: string;
  description?: string;
  /** 低龄模式：按钮更大、用情绪标记替代文字 */
  kid?: boolean;
  /** 只读（viewer 家长 / 学生自己看家长的点评） */
  readOnly?: boolean;
  studentId?: string;
}

export function CommentBox({
  targetType,
  targetId,
  title = '点评',
  description,
  kid = false,
  readOnly = false,
  studentId,
}: CommentBoxProps) {
  const toast = useToast();
  const qc = useQueryClient();
  const [content, setContent] = useState('');
  const [mood, setMood] = useState<CommentMood | null>(null);
  const [error, setError] = useState<string | null>(null);

  const queryKey = ['comments', targetType, targetId ?? 'none', studentId ?? ''] as const;

  const listQuery = useQuery({
    queryKey,
    // 必须把 studentId 传下去：`period` 类型的点评后端无法从 targetId 反查学生，
    // 缺它就返回 400（表现为"点评区永远空的"）。详见 comments.ts 的注释。
    queryFn: () => listComments(targetType, targetId as string, studentId),
    enabled: Boolean(targetId),
    retry: 1,
  });

  const createMutation = useMutation({
    mutationFn: (vars: { content: string; mood: CommentMood | null }) =>
      createComment({
        targetType,
        targetId: targetId as string,
        content: vars.content,
        mood: vars.mood,
        // 同上：period 类型必须带 studentId，否则发表点评也会 400
        ...(studentId ? { studentId } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
    },
  });

  const notReady = listQuery.data?.notReady ?? false;
  const comments = listQuery.data?.comments ?? [];

  const submit = async (text: string) => {
    const value = text.trim();
    if (!value) {
      setError('请先写一句话，或者点下面的快捷短语');
      return;
    }
    setError(null);
    try {
      await createMutation.mutateAsync({ content: value, mood });
      setContent('');
      setMood(null);
      toast.showToast('已发送，孩子端立刻能看到', 'success');
    } catch (err) {
      // 接口未就绪时给一句人话，而不是抛未捕获异常
      setError(toErrorMessage(err));
    }
  };

  return (
    <Card className={kid ? 'ui-child' : undefined}>
      <CardHeader title={title} description={description} />

      {listQuery.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : notReady ? (
        <NotReadyState feature="点评" compact />
      ) : (
        <>
          {comments.length > 0 && (
            <ul className="mb-4 space-y-2">
              {comments.map((comment) => (
                <li key={comment.id} className="rounded-xl bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                    <span className="font-medium text-slate-700">
                      {comment.authorNickname ??
                        (comment.authorType === 'parent' ? '家长' : comment.authorType === 'student' ? '孩子' : '系统')}
                    </span>
                    {comment.mood && <span>{MOOD_LABEL[comment.mood]}</span>}
                    <span>{formatDateTimeFull(comment.createdAt)}</span>
                  </div>
                  <p className={cn('mt-1 text-slate-700', kid ? 'text-lg' : 'text-sm')}>
                    {comment.content}
                  </p>
                </li>
              ))}
            </ul>
          )}

          {!readOnly && (
            <div className="space-y-3 border-t border-slate-100 pt-3">
              {/* 快捷短语：降低家长的操作成本 */}
              <div className="flex flex-wrap gap-2">
                {QUICK_PHRASES.map((phrase) => (
                  <button
                    key={phrase}
                    type="button"
                    onClick={() => setContent(phrase)}
                    className={cn(
                      'rounded-xl bg-brand-50 font-medium text-brand-700 hover:bg-brand-100',
                      kid ? 'min-h-[48px] px-4 text-base' : 'px-3 py-1.5 text-sm',
                    )}
                  >
                    {phrase}
                  </button>
                ))}
              </div>

              {/* 低龄模式：图形化情绪标记 */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">心情：</span>
                {(['great', 'good', 'keep_going'] as CommentMood[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={mood === value}
                    onClick={() => setMood(mood === value ? null : value)}
                    className={cn(
                      'rounded-xl transition-colors',
                      kid ? 'min-h-[48px] px-3 text-xl' : 'px-2.5 py-1 text-sm',
                      mood === value ? 'bg-amber-100 ring-1 ring-amber-300' : 'bg-slate-100 hover:bg-slate-200',
                    )}
                  >
                    {MOOD_LABEL[value]}
                  </button>
                ))}
              </div>

              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={1000}
                rows={kid ? 2 : 3}
                placeholder="写点什么给孩子 —— 具体一点更有用，比如「今天读书时很专心」"
                aria-label="点评内容"
                className={cn(
                  'w-full rounded-xl border border-slate-300 p-3 text-slate-900 placeholder:text-slate-400',
                  'focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100',
                  kid ? 'text-lg' : 'text-sm',
                )}
              />

              {error && (
                <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {error}
                </p>
              )}

              <div className="flex justify-end">
                <Button
                  size={kid ? 'kid' : 'md'}
                  loading={createMutation.isPending}
                  onClick={() => void submit(content)}
                >
                  发送点评
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
